import { useEffect, useRef } from "react"

/**
 * A single deliberate "are you sure?" for actions that spend a large stretch of
 * days at once (Phase 10's "Confirmation before long actions"). Days are the
 * game's only real currency, and a mis-clicked ten-day march is a real loss;
 * this stands in front of one when the player has asked it to. Enter confirms,
 * Escape cancels, and focus lands on Confirm so the keyboard flow is complete.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string
  body: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const confirmRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    confirmRef.current?.focus()
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault()
        onCancel()
      } else if (event.key === "Enter") {
        event.preventDefault()
        onConfirm()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [onConfirm, onCancel])

  return (
    <div
      className="confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
    >
      <div className="confirm-dialog" onClick={(event) => event.stopPropagation()}>
        <h2>{title}</h2>
        <p>{body}</p>
        <div className="confirm-actions">
          <button ref={confirmRef} type="button" className="danger" onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button type="button" onClick={onCancel}>
            Not yet
          </button>
        </div>
      </div>
    </div>
  )
}
